<template>
  <div v-if="shouldShow">
    <!-- Personal Information Card -->
    <div class="info-card">
      <div class="section-header">
        <svg 
          class="section-icon red-icon" 
          width="20" 
          height="20" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          viewBox="0 0 24 24">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle 
            cx="12" 
            cy="7" 
            r="4"/>
        </svg>
        <h3>Personal Information</h3>
      </div>
      <div class="info-content">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Name:</span> 
            <span class="info-value">{{ customerInfo.name || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Email:</span> 
            <span class="info-value">{{ customerInfo.email || '-' }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">Contact:</span> 
            <span class="info-value">{{ customerInfo.contact || '-' }}</span>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Address Information Card -->
    <div class="info-card">
      <div class="section-header">
        <svg 
          class="section-icon red-icon" 
          width="20" 
          height="20" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          viewBox="0 0 24 24">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle 
            cx="12" 
            cy="10" 
            r="3"/>
        </svg>
        <h3>Address Information</h3>
      </div>
      <div class="info-content">
        <div class="info-grid">
          <div class="info-item full-width">
            <span class="info-label">Address:</span> 
            <span class="info-value">{{ customerInfo.address || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Address Line 2:</span> 
            <span class="info-value">{{ customerInfo.address_2 || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Address Line 3:</span> 
            <span class="info-value">{{ customerInfo.address_3 || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">City:</span> 
            <span class="info-value">{{ customerInfo.city || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">State:</span> 
            <span class="info-value">{{ displayStateName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Country:</span> 
            <span class="info-value">{{ displayCountryName }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Postal Code:</span> 
            <span class="info-value">{{ customerInfo.postal_zone || '-' }}</span>
          </div>
        </div>
      </div>
    </div>
  
    <!-- Identification & Tax Information Card -->
    <div class="info-card">
      <div class="section-header">
        <svg 
          class="section-icon red-icon" 
          width="20" 
          height="20" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          viewBox="0 0 24 24">
          <rect 
            x="1" 
            y="4" 
            width="22" 
            height="16" 
            rx="2" 
            ry="2"/>
          <line 
            x1="1" 
            y1="10" 
            x2="23" 
            y2="10"/>
        </svg>
        <h3>Identification & Tax Information</h3>
      </div>
      <div class="info-content">
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">ID Type:</span> 
            <span class="info-value">{{ customerInfo.id_type || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">ID Number:</span> 
            <span class="info-value">{{ customerInfo.id_no || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">TIN:</span> 
            <span class="info-value">{{ customerInfo.enter_id || '-' }}</span>
          </div>
          <div 
            v-if="customerInfo.id_type === 'BRN'" 
            class="info-item">
            <span class="info-label">SST Reg. No:</span> 
            <span class="info-value">{{ customerInfo.sst_reg_no || '-' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
import stateData from '../../../../data/einvoice/state_codes.json';
import countryData from '../../../../data/einvoice/country_codes.json';

export default {
    name: 'CustomerInfoSection',
    props: {
        alreadySubmitted: {
            type: Boolean,
            required: true,
        },
        isExpired: {
            type: Boolean,
            required: true,
        },
        status: {
            type: String,
            default: 'Submitted',
        },
        customerInfo: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            states: stateData,
            countries: countryData,
        };
    },
    computed: {
        shouldShow() {
            return (
                this.alreadySubmitted &&
                !this.isExpired &&
                this.customerInfo &&
                (this.status === 'Valid' ||
                    this.status === 'Invalid' ||
                    this.status === 'Submitted')
            );
        },
        displayStateName() {
            if (!this.customerInfo) return '-';

            // Try to find by stateCode first
            if (this.customerInfo.stateCode) {
                const state = this.states.find(
                    s => s.Code === this.customerInfo.stateCode,
                );
                if (state) return state.State;
            }

            // If no stateCode, try to find by state value (in case it's a code)
            if (this.customerInfo.state) {
                const stateByCode = this.states.find(
                    s => s.Code === this.customerInfo.state,
                );
                if (stateByCode) return stateByCode.State;

                // If not found by code, return the state value as is
                return this.customerInfo.state;
            }

            return '-';
        },
        displayCountryName() {
            if (!this.customerInfo) return '-';

            // Try to find by countryCode first
            if (this.customerInfo.countryCode) {
                const country = this.countries.find(
                    c => c.Code === this.customerInfo.countryCode,
                );
                if (country) return country.Country;
            }

            // If no countryCode, try to find by country value (in case it's a code)
            if (this.customerInfo.country) {
                const countryByCode = this.countries.find(
                    c => c.Code === this.customerInfo.country,
                );
                if (countryByCode) return countryByCode.Country;

                // If not found by code, return the country value as is
                return this.customerInfo.country;
            }

            return '-';
        },
    },
};
</script>
  
  <style scoped>
.info-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
    overflow: hidden;
}

.section-header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
}

.section-icon {
    color: #ef5350;
    margin-right: 12px;
    flex-shrink: 0;
}

.section-header h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.info-content {
    padding: 24px;
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}

@media (min-width: 768px) {
    .info-grid {
        grid-template-columns: 1fr 1fr;
    }
    .info-item.full-width {
        grid-column: 1 / -1;
    }
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.info-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 2px;
}

.info-value {
    font-size: 0.95rem;
    color: #1e293b;
    word-break: break-word;
    font-weight: 500;
}

@media (max-width: 768px) {
    .section-header {
        padding: 16px 20px;
    }
    .info-content {
        padding: 16px;
    }
    .info-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/components/CustomerInfoSection.vue